import React, {useEffect, useMemo, useState} from "react";
import {faArrowLeft, faXmark} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../../components/IconButton";
import Heading from "../../../components/Heading";
import {useTranslation} from "react-i18next";
import {useGlobalContext} from "../../../contexts/globalContext";
import {Media, MediaCategory, MediaWithCategories} from "../../../types/Media";
import cx from "classnames";
import styles from "./MediaSettings.module.css";
import Loader from "../../../components/Loader";
import CreateMediaCard from "./CreateMediaCard";
import MediaCard from "./MediaCard";
import NewMediaModalContent from "./NewMediaModalContent";
import EditMediaModalContent from "./EditMediaModalContent";
import DeleteMediaModalContent from "./DeleteMediaModalContent";
import Select from "../../../components/Select";
import Text from "../../../components/Text";
import {useFetchData} from "../../../hooks/useFetchData";
import {treeOptions} from "../../../helpers/treeOptions";
import Card from "../../../components/Card";
import {useAdmin} from "../../../contexts/AdminContext";

const MediaSettings: React.FC<{changeSettingsTab: (tab: string|null) => void}> = ({changeSettingsTab}) => {
    const {t} = useTranslation(['settings']);
    const {subscription} = useAdmin()

    const {
        data: media,
        fetch: fetchMedia,
        isLoading: isMediaLoading
    } = useFetchData<MediaWithCategories>('media')

    const {
        data: categories,
        fetch: fetchCategories
    } = useFetchData<MediaCategory>('mediaCategories')

    const {showModal} = useGlobalContext()
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);

    const selectedCategories = useMemo(() => {
        return categories.filter((category) => {
            return selectedCategoryIds.includes(Number(category.id))
        })
    }, [categories, selectedCategoryIds])

    useEffect(() => {
        fetchMedia()
    }, [fetchMedia])

    useEffect(() => {
        fetchCategories()
    }, [fetchCategories])

    const selectOptions = useMemo(() => {
      return treeOptions<MediaCategory>(
          categories,
          null,
          'name',
          'id',
          'parent_id',
          'id',
          0
      )
    }, [categories])

    const filteredMedia: MediaWithCategories[] = useMemo(() => {
        return (selectedCategoryIds.length > 0) ? media.filter((mediaElement) => {
            return mediaElement.media_category_ids.some((cat_id) => {
                return selectedCategoryIds.includes(cat_id)
            })
        }) : media
    }, [media, selectedCategoryIds])

    const onCategorySelect = (category_id: number) => {
        setSelectedCategoryIds(prevCategoryIds => [...prevCategoryIds, category_id]);
    }

    const onCategoryRemove = (category_id: number) => {
        setSelectedCategoryIds(prevCategoryIds => {
            return prevCategoryIds.filter((cat_id) => cat_id !== category_id)
        });
    }

    const openCreateModal = () => {
        const content: JSX.Element = <NewMediaModalContent
            onSubmit={() => {
                fetchCategories()
                fetchMedia()
            }}
        />
        showModal({heading: t('media.create_modal.heading'), content: content, fullscreen: false})
    }

    const openEditModal = (mediaElement: MediaWithCategories) => {
        const content: JSX.Element = <EditMediaModalContent
            media={mediaElement}
            onSubmit={() => {
                fetchCategories()
                fetchMedia()
            }}
        />
        showModal({heading: t('media.create_modal.heading'), content: content, fullscreen: false})
    }

    const openDeleteModal = (mediaElement: Media) => {
        const content: JSX.Element = <DeleteMediaModalContent media={mediaElement} onSubmit={() => fetchMedia()}/>
        showModal({heading: t('media.delete_modal.heading'), content: content, fullscreen: false})
    }

    return (
        <>
            <Heading level={3} classes={'my-4'}>
                {t('media.heading')}
            </Heading>
            <IconButton
                icon={faArrowLeft}
                color={"black"}
                onClick={() => changeSettingsTab(null)}
            >
                {t('common.backToOverview')}
            </IconButton>
            <Text classes={'mt-3 mb-6'}>
                {t('media.description')}
            </Text>
            {subscription?.plans.filter((p) => p.slug === 'zr-customizer-mediathek').length === 0 &&
                <Card classes={cx(styles['card'])}>
                    {t('subscription.noMedia')}
                </Card>
            }
            <div className={cx(styles['selected-filter'])}>
                <Select
                    options={selectOptions}
                    onSelect={(property: string, value: string) => onCategorySelect(parseInt(value))}
                    property="category"
                    value={""}
                    placeholder={t('media.filter_by_category')}
                    classes={cx(styles['select-element'], 'mr-4 mb-6 w-auto sm:mb-0')}
                    searchable={true}
                    searchTermUpdate={true}
                />
                <div className={cx(styles['selected-categories-filter'])}>
                    {selectedCategories.map((category) => (
                        <div key={category.id} className={cx(styles['selected-categories-elements-filter'])}>
                            <Text>{category.name}</Text>
                            <IconButton
                                icon={faXmark}
                                color={'#2f2f2f'}
                                onClick={() => onCategoryRemove(Number(category.id))}
                            />
                        </div>
                    ))}
                </div>
            </div>
            {!isMediaLoading &&
                <div className={cx(styles['media-list'])}>
                    <CreateMediaCard openCreateModal={openCreateModal} />
                    {filteredMedia.map((mediaElement) => {
                        return (
                            <MediaCard
                                key={mediaElement.id}
                                {...mediaElement}
                                openEditModal={() => openEditModal(mediaElement)}
                                openDeleteModal={() => openDeleteModal(mediaElement)}
                                categories={categories}
                            />
                        )}
                        )
                    }
                </div>
            }
            {isMediaLoading &&
                <div className={cx(styles['loading-wrapper'])}>
                    <Loader isLoading={isMediaLoading} size={'m'} />
                </div>
            }
        </>
    )
}

export default MediaSettings
